<template>
  <v-card color="alternate" class="text-center pa-5" data-test="stripePayments">
    <section class="stripe-container mx-auto">
      <div class="text-right">
        <v-btn
          v-if="$allowedMutation(payment.allowedMutations, 'paymentCancel')"
          class="stripe-cancel"
          text
          :disabled="loading || paymentSuccessLoading || paymentCancelling"
          @click="() => cancelPayment()"
        >
          <v-icon class="mr-2" color="red">$iconTimesCircle</v-icon>
          {{ $t("common:button.cancel", "Cancel", "Cancel button") }}
        </v-btn>
      </div>
      <div v-show="paymentSuccessLoading == false">
        <form id="payment-form">
          <div id="payment-element">
            <!-- Elements will create form elements here -->
          </div>

          <div v-show="!!error" id="error-message" class="alert pa-4 my-4">
            <!-- Display error message to your customers here -->
            {{ error }}
          </div>

          <v-btn
            v-if="stripeIsMounted"
            color="accent"
            large
            class="my-3"
            :disabled="loading || paymentCancelling"
            :loading="loading"
            data-test="submitPayment"
            block
            min-width="140px"
            @click="() => submitPayment()"
          >
            {{
              $t(
                "people:ShoppingBasket.button.submitPayment.label",
                "Pay",
                "Label for the button to submit the payment"
              )
            }}
          </v-btn>
        </form>
      </div>

      <div
        v-show="paymentSuccessLoading == true"
        data-test="finalizingPurchase"
        class="pa-5"
      >
        <v-progress-circular
          v-if="paymentSuccessLoading"
          color="black"
          indeterminate
          width="6"
          size="54"
        />

        <div class="text-center pa-4">
          <h2 style="font-size: 18px;">
            {{ $t("people:ShoppingBasket.message.finalizingPurchase") }}
          </h2>
        </div>
      </div>
    </section>
  </v-card>
</template>
<script>
import { defineComponent } from "vue";
import { loadStripe } from "@stripe/stripe-js";
import gql from "fraql";
import { getFederationConfiguration } from "@composables/masterdata/config";

export default defineComponent({
  name: "PaymentStripeAnyMethod",
  props: {
    clientSecret: {
      type: String,
      required: true
    },
    paymentId: {
      type: String,
      required: true
    },
    payment: {
      type: Object,
      required: true
    },
    shoppingBasketId: {
      type: String,
      default: null
    },
    amountCents: {
      type: Number,
      default: 0
    },
    onBehalfOfConnectedStripeAccountId: {
      type: String,
      default: null
    }
  },
  setup() {
    return {
      ...getFederationConfiguration()
    };
  },
  data() {
    return {
      stripeIsMounted: false,
      stripe: "",
      elements: [],
      card: "",
      loading: false,
      error: "",
      paymentSuccessLoading: false,
      paymentCancelling: false
    };
  },
  mounted() {
    this.configureStripe();
  },
  apollo: {
    serverSidePaid: {
      query() {
        return gql`
          query stripeServerSidePaidInterval($id: ID!, $shoppingBasketId: ID) {
            payment(id: $id, shoppingBasketId: $shoppingBasketId) {
              id
              status
            }
          }
        `;
      },
      skip() {
        return this.paymentSuccessLoading == false;
      },
      manual: true,
      variables() {
        return {
          id: this.paymentId,
          shoppingBasketId: this.shoppingBasketId
        };
      },
      result({ data, loading }) {
        if (
          !loading &&
          ["PAID", "PAID_TO_RETURNS"].includes(data.payment.status)
        ) {
          this.$emit("paymentSuccessful");
          this.$apollo.queries.serverSidePaid.stopPolling();
          this.paymentSuccessLoading = false;
        }
      },
      pollInterval: 5000,
      fetchPolicy: "network-only"
    }
  },
  methods: {
    /**
     * Configures Stripe by setting up the elements and creating the card element.
     */
    async configureStripe() {
      if (this.elements.length > 0) {
        this.stripeIsMounted = true;
        // Must mean its already mounted
        return;
      }
      //console.log("Configuring Stripe Elements");
      const locale = document.querySelector("html");
      let stripeOptions = {
        locale: locale.lang.split("-")[0]
      };

      // according to Stripe docs we should provide stripeAccount in case of Connect payment but when providing, it will not work
      // if (this.onBehalfOfConnectedStripeAccountId)
      //   stripeOptions.stripeAccount = this.onBehalfOfConnectedStripeAccountId;

      this.stripe = await loadStripe(
        process.env.VUE_APP_PAYMENT_STRIPE_CONNECT_PUBLISABLE_KEY,
        stripeOptions
      );

      await this.$nextTick();

      const options = {
        clientSecret: this.clientSecret,
        // Fully customizable with appearance API.
        appearance: {}
      };
      this.elements = this.stripe.elements(options);
      this.paymentElement = this.elements.create("payment", {
        layout: "accordion"
      });

      this.paymentElement.on("ready", () => {
        this.stripeIsMounted = true;
      });
      this.paymentElement.mount("#payment-element");

      /*this.paymentElement.addEventListener("change", ({ error }) => {
        console.log(error);
        const displayError = document.getElementById("error-message");
        if (error) {
          displayError.textContent = error.message;
        } else {
          displayError.textContent = "";
        }
      });*/
      await this.$nextTick();
    },
    async submitPayment() {
      //console.log("Submitting payment");
      this.loading = true;
      this.error = "";
      const elements = this.elements;

      try {
        const response = await this.stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: window.location.href
          },
          redirect: "if_required"
        });

        console.log("Payment response", response);

        if (response.error && response.error.type == "validation_error") {
          this.loading = false;
          return;
        }

        if (response.error && response.error.payment_method.type == "card") {
          // Show error to your customer (e.g., insufficient funds)
          this.error =
            `${this.$t(
              "people:shoppingBasket.feedback.payment_failed_with_message",
              "Payment has failed with following error:"
            )} ` + response.error.payment_intent.last_payment_error.message;
          this.$emit("paymentFailed");
        } else if (
          response.error &&
          response.error.payment_method.type != "card"
        ) {
          // Show generic error
          this.error = this.$t(
            "shoppingBasket.feedback.payment_failed",
            "Payment failed, please try again or try another method"
          );
          this.$emit("paymentFailed");
        } else if (response.paymentIntent) {
          // The payment has been processed!
          if (response.paymentIntent.status === "succeeded") {
            this.paymentSuccessLoading = true;
          }
        }
      } catch (error) {
        this.$root.$snackbarServerError(error, { level: "critical" });
      } finally {
        this.loading = false;
      }
    },
    async cancelPayment() {
      try {
        this.paymentCancelling = true;

        await this.$apollo.mutate({
          mutation: gql`
            mutation paymentCancel($paymentId: ID!) {
              paymentCancel(input: { paymentId: $paymentId }) {
                payment {
                  id
                  status
                }
              }
            }
          `,
          variables: {
            paymentId: this.paymentId
          }
        });

        this.$emit("closeForm");
      } catch (error) {
        this.$root.$snackbarServerError(error, { level: "error" });
      } finally {
        this.paymentCancelling = false;
      }
    }
  }
});
</script>
<style scoped>
/**
* The CSS shown here will not be introduced in the Quickstart guide, but
* shows how you can use CSS to style your Element's container.
*/
.stripe-cancel {
  right: 0px;
  top: 0px;
  transform: translateY(-7px);
}
.stripe-container {
  width: 45%;
  display: flex;
  flex-direction: column;
  position: relative;
}
@media (max-width: 768px) {
  .stripe-container {
    width: 65%;
  }
}

@media (max-width: 568px) {
  .stripe-container {
    width: 95%;
  }
  /*.stripe-cancel {
    position: static;
  }*/
}

input {
  padding: 16px;
}
input,
.StripeElement {
  color: #32325d;
  background-color: var(--v-alternate-base);
  /*border: 1px solid transparent;*/
  border-radius: 4px;

  /*box-shadow: 0 1px 3px 0 #e6ebf1;*/
  -webkit-transition: box-shadow 150ms ease;
  transition: box-shadow 150ms ease;
}

input {
  padding: 10px 12px;
}

input:focus,
.StripeElement--focus {
  box-shadow: 0 1px 3px 0 #cfd7df;
}

.StripeElement--invalid {
  border-color: #fa755a;
}

.StripeElement--webkit-autofill {
  background-color: #fefde5 !important;
}
</style>
